// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { auth } from "../firebase"; // compat auth
import { Api } from "../lib/api";

const AuthContext = createContext({
  currentUser: null,
  loading: true,
  setCurrentUser: () => {},
  logout: async () => {},
});

const LOGOUT_FENCE_KEY = "nz_logoutFenceUntil";

// ✅ Client-side MFA gate flags (must match LoginPage)
const MFA_PENDING_KEY = "nx_mfa_pending";

function isLogoutFenced() {
  const until = parseInt(localStorage.getItem(LOGOUT_FENCE_KEY) || "0", 10);
  return Number.isFinite(until) && until > Date.now();
}

function isClientMfaPending() {
  try {
    return sessionStorage.getItem(MFA_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

async function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Retry cookie->firebase upgrade for a short window.
 * This prevents "instant bounce to /login" when /api/me is briefly 401 right after login / deep link.
 */
async function retryUpgrade(upgradeFn, { tries = 4, delayMs = 350 } = {}) {
  let last = null;

  for (let i = 0; i < tries; i++) {
    last = await upgradeFn();
    if (last?.ok) return last;

    // MFA required => user action needed, stop retrying
    if (last?.reason === "mfa_required") return last;

    // Transient reasons worth retrying
    const transient = new Set([
      "no_cookie_session",
      "upgrade_inflight",
      "token_endpoint_failed",
      "firebase_signin_failed",
    ]);

    if (transient.has(last?.reason)) {
      await sleep(delayMs);
      continue;
    }

    // Not transient: break
    break;
  }

  return last;
}

/**
 * Optional fallback: normalize cookie user so UI can still render (non-admin flows),
 * but the target state is always a real Firebase session.
 */
function normalizeCookieUser(u) {
  if (!u) return null;
  return {
    uid: String(u.id ?? u.email ?? u.phone ?? "cookie-user"),
    email: u.email ?? null,
    phoneNumber: u.phone ?? null,
    providerData: [{ providerId: "custom" }],
    isAnonymous: false,
    _source: "cookie",
  };
}

function getCurrentPath() {
  try {
    return window.location.pathname || "/";
  } catch {
    return "/";
  }
}

function isPublicGuestRoute(pathname) {
  const p = String(pathname || "/").toLowerCase();

  return (
    p === "/" ||
    p === "/market" ||
    p === "/market/browse" ||
    p.startsWith("/trade/") ||
    p === "/notifications" ||
    p === "/trust-safety" ||
    p === "/faq" ||
    p === "/help" ||
    p === "/terms" ||
    p === "/privacy" ||
    p === "/help/how-trades-work" ||
    p === "/help/why-no-direct-chat"
  );
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // small guard to prevent concurrent upgrades (common during route changes)
  const upgradeState = useMemo(() => ({ inflight: false }), []);

  const upgradeCookieToFirebase = useCallback(async () => {
    // ✅ CRITICAL: while MFA is pending, do not attempt upgrade
    // (server will reject with 428 anyway; this prevents loops and accidental "auth")
    if (isClientMfaPending()) return { ok: false, reason: "mfa_required" };

    // Already signed into Firebase
    if (auth.currentUser?.uid) return { ok: true, user: auth.currentUser };

    if (upgradeState.inflight) return { ok: false, reason: "upgrade_inflight" };
    upgradeState.inflight = true;

    try {
      // Confirm cookie session exists (guest-safe; no 401 spam)
      let me;
      try {
        me = await Api.meOptional(); // { authenticated, user } OR { authenticated:false }
      } catch (err) {
        return { ok: false, reason: "no_cookie_session", error: err };
      }

      if (!me?.authenticated || !me?.user) {
        return { ok: false, reason: "no_cookie_session" };
      }

      // Mint Firebase custom token from cookie
      let tok;
      try {
        tok = await Api.sessionFirebaseCustomToken(); // { ok, token, uid } OR throws
      } catch (err) {
        // If server says MFA is required, do NOT fallback to cookie user.
        if (err?.status === 428 || err?.response?.error === "mfa_required") {
          return { ok: false, reason: "mfa_required" };
        }
        return { ok: false, reason: "token_endpoint_failed", error: err };
      }

      if (!tok?.ok || !tok?.token) {
        if (tok?.error === "mfa_required") return { ok: false, reason: "mfa_required" };
        return {
          ok: false,
          reason: tok?.error || "token_missing",
          fallbackUser: normalizeCookieUser(me.user),
        };
      }

      // Establish Firebase session
      await auth.signInWithCustomToken(tok.token);
      await auth.currentUser?.reload().catch(() => {});
      if (auth.currentUser?.uid) {
        return { ok: true, user: auth.currentUser };
      }

      return {
        ok: false,
        reason: "firebase_signin_failed",
        fallbackUser: normalizeCookieUser(me.user),
      };
    } finally {
      upgradeState.inflight = false;
    }
  }, [upgradeState]);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = auth.onAuthStateChanged(
      async (fbUser) => {
        if (cancelled) return;

        // ✅ CRITICAL: if MFA pending, do NOT treat Firebase provider user as authenticated
        if (isClientMfaPending()) {
          // Best-effort: ensure no provider session can be used by other code paths
          // (LoginPage already signs out; this just closes any race window)
          if (fbUser?.uid) {
            try { await auth.signOut().catch(() => {}); } catch {}
          }

          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // Case A: Firebase session exists
        if (fbUser?.uid) {
          setCurrentUser(fbUser);
          setLoading(false);
          return;
        }

        // Case B: no Firebase session — try upgrade cookie -> Firebase
        let hasRedirectIntent = false;
        try {
          hasRedirectIntent = !!localStorage.getItem("postLoginRedirect");
        } catch {
          hasRedirectIntent = false;
        }

        if (hasRedirectIntent) {
          await new Promise((r) => setTimeout(r, 250));
          if (cancelled) return;
          if (auth.currentUser?.uid) {
            setCurrentUser(auth.currentUser);
            setLoading(false);
            return;
          }
        }

       if (isLogoutFenced()) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        const pathname = getCurrentPath();
        const allowGuestMode = isPublicGuestRoute(pathname) && !hasRedirectIntent;

        if (allowGuestMode) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        const upgraded = await retryUpgrade(upgradeCookieToFirebase, {
          tries: hasRedirectIntent ? 6 : 4,
          delayMs: 350,
        });

        if (cancelled) return;

        if (upgraded?.ok && upgraded.user?.uid) {
          setCurrentUser(upgraded.user);
          setLoading(false);
          return;
        }

        // If MFA is required, treat as NOT authenticated (prevents bypass UX)
        if (upgraded?.reason === "mfa_required") {
          setCurrentUser(null);
          setLoading(false);
          return;
        }

        // Optional fallback for non-admin browsing: allow cookie-normalized user
        if (upgraded?.fallbackUser) {
          setCurrentUser(upgraded.fallbackUser);
          setLoading(false);
          return;
        }

        setCurrentUser(null);
        setLoading(false);
      },
      (err) => {
        console.error("onAuthStateChanged error:", err);
        if (!cancelled) {
          setCurrentUser(null);
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [upgradeCookieToFirebase]);

  const logout = useCallback(async () => {
    try {
      // fence for 2 seconds
      try { localStorage.setItem(LOGOUT_FENCE_KEY, String(Date.now() + 2000)); } catch {}

      // ✅ also clear client-side MFA flag on explicit logout
      try { sessionStorage.removeItem(MFA_PENDING_KEY); } catch {}

      await auth.signOut().catch(() => {});
      try { await Api.logout(); } catch {}
    } finally {
      setCurrentUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ currentUser, setCurrentUser, loading, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
